import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    EMPTY_COMPARE_PAIR,
    reseedCompareScans,
    resolvePairSteps,
    selectComparePhoto,
    selectCompareScan,
    type CompareSide,
    type CompareSteps
} from './comparePhotoPairing';
import type { DmScanSummary } from './types';
import { useDmScanDetail, type DmScansQueryConfig } from './useDmScansQueries';
import { useScanPhotos, type ScanPhotoCarousel } from './useScanPhotos';

export interface ComparePaneState {
    scanId: number | null;
    scan: DmScanSummary | null;
    isLoading: boolean;
    carousel: ScanPhotoCarousel;
    photoIndex: number;
    onSelectScan: (scanId: number) => void;
    onSelectPhoto: (index: number) => void;
}

export type ComparePaneStates = Record<CompareSide, ComparePaneState>;

interface CompareSideData {
    isLoading: boolean;
    carousel: ScanPhotoCarousel;
}

type CompareSideHandlers = Pick<ComparePaneState, 'onSelectScan' | 'onSelectPhoto'>;

function useCompareSideData(
    queries: DmScansQueryConfig,
    patientId: string | null,
    scanId: number | null
): CompareSideData {
    const { data, isLoading } = useDmScanDetail(queries, patientId, scanId);
    return { isLoading, carousel: useScanPhotos(data?.photos) };
}

function indexOfStep(steps: readonly string[], step: string | null): number {
    const index = step === null ? -1 : steps.indexOf(step);
    return Math.max(index, 0);
}

// Both panes' steps live here so a pick on one side pairs the other in the same state update.
export function useComparePair(
    queries: DmScansQueryConfig,
    patientId: string | null,
    ordered: DmScanSummary[]
): ComparePaneStates {
    const [pair, setPair] = useState(EMPTY_COMPARE_PAIR);
    const left = useCompareSideData(queries, patientId, pair.left.scanId);
    const right = useCompareSideData(queries, patientId, pair.right.scanId);

    useEffect(() => {
        const ids = ordered.map((scan) => scan.scanId);
        setPair((prev) => reseedCompareScans(prev, ids));
    }, [ordered]);

    const steps = useMemo<CompareSteps>(
        () => ({ left: left.carousel.steps, right: right.carousel.steps }),
        [left.carousel.steps, right.carousel.steps]
    );
    const resolved = resolvePairSteps(pair, steps);

    const selectScan = useCallback(
        (side: CompareSide, scanId: number) =>
            setPair((prev) => selectCompareScan(prev, side, scanId, steps)),
        [steps]
    );
    const selectPhoto = useCallback(
        (side: CompareSide, index: number) => {
            const step = steps[side][index];
            if (step === undefined) return;
            setPair((prev) => selectComparePhoto(prev, side, step, steps));
        },
        [steps]
    );

    const handlers = useMemo<Record<CompareSide, CompareSideHandlers>>(() => {
        const forSide = (side: CompareSide): CompareSideHandlers => ({
            onSelectScan: (scanId) => selectScan(side, scanId),
            onSelectPhoto: (index) => selectPhoto(side, index)
        });
        return { left: forSide('left'), right: forSide('right') };
    }, [selectScan, selectPhoto]);

    const paneState = (side: CompareSide, data: CompareSideData): ComparePaneState => ({
        ...handlers[side],
        ...data,
        scanId: pair[side].scanId,
        scan: ordered.find((scan) => scan.scanId === pair[side].scanId) ?? null,
        photoIndex: indexOfStep(steps[side], resolved[side])
    });

    return { left: paneState('left', left), right: paneState('right', right) };
}
